import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CheckIcon, StarIcon, UserIcon, CurrencyPoundIcon } from '@heroicons/react/24/outline';
import PublicLayoutWrapper from '../components/PublicLayoutWrapper';
import CountryCitySelect from '../components/CountryCitySelect';
import config from '../config';
import Helper from '../helpers';

const apiBaseUrl = config.apiBaseUrl;

const FOUNDER_FREE_LIMIT = 20;
const EARLYBIRD_LIMIT = 100;

const SERVICE_OPTIONS = [
    { key: 'diet_meal_plans',      label: 'Diet / Meal Plans' },
    { key: 'sports_massage',       label: 'Sports Massage' },
    { key: 'consultations',        label: 'Consultations' },
    { key: 'online_coaching',      label: 'Online Coaching' },
    { key: 'group_sessions',       label: 'Group Sessions' },
    { key: 'nutrition_advice',     label: 'Nutrition Advice' },
    { key: 'body_composition',     label: 'Body Composition Testing' },
    { key: 'rehabilitation',       label: 'Rehabilitation / Injury Support' },
    { key: 'pre_post_natal',       label: 'Pre / Post Natal' },
    { key: 'mindset_coaching',     label: 'Mindset & Wellbeing Coaching' },
    { key: 'strength_conditioning',label: 'Strength & Conditioning' },
    { key: 'youth_training',       label: 'Youth / Junior Training' },
];

const PTDirectorySignup = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [founderFreeSpotsRemaining, setFounderFreeSpotsRemaining] = useState(FOUNDER_FREE_LIMIT);
    const [earlybirdSpotsRemaining, setEarlybirdSpotsRemaining] = useState(EARLYBIRD_LIMIT);
    const [selectedPlan, setSelectedPlan] = useState('pt-directory');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const [form, setForm] = useState({
        name: '',
        email: '',
        country: 'United Kingdom',
        city: '',
        postcode: '',
        bio: '',
        specialisms: '',
        priceType: 'from',   // 'from' | 'range' | ''
        priceFrom: '',
        priceTo: '',
    });
    const [selectedServices, setSelectedServices] = useState([]);

    useEffect(() => {
        const init = async () => {
            const token = await Helper.getCookie('wh-jwt');
            setIsLoggedIn(!!token);

            try {
                const response = await fetch(`${apiBaseUrl}/api/ptdirectory/plans`);
                if (response.ok) {
                    const data = await response.json();
                    setPlans(data.plans || []);
                    const founderFree = data.founderFreeSpotsRemaining ?? FOUNDER_FREE_LIMIT;
                    const earlybird = data.earlybirdSpotsRemaining ?? EARLYBIRD_LIMIT;
                    setFounderFreeSpotsRemaining(founderFree);
                    setEarlybirdSpotsRemaining(earlybird);
                    // Auto-select the best available plan
                    if (founderFree > 0) {
                        setSelectedPlan('pt-directory-founder');
                    } else if (earlybird > 0) {
                        setSelectedPlan('pt-directory-earlybird');
                    } else {
                        setSelectedPlan('pt-directory');
                    }
                }
            } catch (err) {
                console.error('Error fetching plans:', err);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const toggleService = (key) => {
        setSelectedServices(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!isLoggedIn) {
            navigate('/login?redirect=/pt-directory-signup');
            return;
        }

        setSubmitting(true);
        try {
            const token = await Helper.getCookie('wh-jwt');
            const payload = {
                name: form.name,
                email: form.email,
                city: form.city,
                postcode: form.postcode,
                bio: form.bio,
                specialisms: form.specialisms,
                priceType: form.priceType || null,
                priceFrom: form.priceFrom ? parseFloat(form.priceFrom) : null,
                priceTo: form.priceType === 'range' && form.priceTo ? parseFloat(form.priceTo) : null,
                services: selectedServices.length > 0 ? selectedServices.join(',') : null,
                requestedPlanId: selectedPlan
            };
            const response = await fetch(`${apiBaseUrl}/api/ptdirectory/register`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                navigate('/find-pt?registered=1');
            } else if (data.status === 'already_registered') {
                setError('You are already listed in the PT directory.');
            } else if (data.status === 'founder_limit_reached') {
                setError('All founder free spots have been taken. Please select the early bird or standard plan.');
                setSelectedPlan(earlybirdSpotsRemaining > 0 ? 'pt-directory-earlybird' : 'pt-directory');
            } else if (data.status === 'earlybird_limit_reached') {
                setError('All early bird spots have been taken. Please select the standard plan.');
                setSelectedPlan('pt-directory');
            } else {
                setError(data.message || 'Something went wrong. Please try again.');
            }
        } catch (err) {
            console.error('Error registering:', err);
            setError('Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const selectedPlanDetails = plans.find(p => p.id === selectedPlan);

    const getPlanBadge = (planId) => {
        if (planId === 'pt-directory-founder') return { label: '🆓 LIMITED OFFER – FREE FOR LIFE', className: 'bg-yellow-500 text-black' };
        if (planId === 'pt-directory-earlybird') return { label: '⭐ LIMITED OFFER – EARLY BIRD', className: 'bg-orange-500 text-white' };
        return null;
    };

    const getPlanSpotsRemaining = (planId) => {
        if (planId === 'pt-directory-founder') return founderFreeSpotsRemaining;
        if (planId === 'pt-directory-earlybird') return earlybirdSpotsRemaining;
        return null;
    };

    return (
        <PublicLayoutWrapper
            title="Join the PT Directory"
            subtitle="Get found by potential clients in your area"
            showBackToHome={true}
        >
            <div className="max-w-4xl mx-auto">
                {/* Limited Offer Banner */}
                {(founderFreeSpotsRemaining > 0 || earlybirdSpotsRemaining > 0) && (
                    <div className="mb-6 bg-gradient-to-r from-yellow-900/40 to-orange-900/40 border border-yellow-600/40 rounded-xl p-4 text-center">
                        <p className="text-yellow-300 font-semibold text-base">
                            🎉 Limited Launch Offer – Be one of our first members and lock in a special price for life!
                        </p>
                        {founderFreeSpotsRemaining > 0 && (
                            <p className="text-yellow-400 text-sm mt-1">
                                Only <strong>{founderFreeSpotsRemaining}</strong> FREE spots remaining (first 20 PTs)
                            </p>
                        )}
                        {earlybirdSpotsRemaining > 0 && (
                            <p className="text-orange-300 text-sm mt-0.5">
                                <strong>{earlybirdSpotsRemaining}</strong> Early Bird spots at £1.99/month still available (positions 21–120)
                            </p>
                        )}
                    </div>
                )}

                {/* Pricing Plans */}
                <div className="mb-10">
                    <h2 className="text-2xl font-bold text-white mb-6 text-center">Choose Your Plan</h2>

                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-400"></div>
                        </div>
                    ) : (
                        <div className="grid gap-4 md:grid-cols-3">
                            {plans.filter(p => p.isAvailable !== false).map(plan => {
                                const badge = getPlanBadge(plan.id);
                                const spotsRemaining = getPlanSpotsRemaining(plan.id);
                                const isSelected = selectedPlan === plan.id;
                                const priceDisplay = plan.price === 0 ? 'FREE' : `£${plan.price}`;
                                const priceSuffix = plan.price === 0 ? 'for life' : '/month';
                                return (
                                    <div
                                        key={plan.id}
                                        onClick={() => setSelectedPlan(plan.id)}
                                        className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all duration-200 ${
                                            isSelected
                                                ? 'border-emerald-500 bg-emerald-900/20 ring-2 ring-emerald-500/20'
                                                : 'border-gray-700 bg-gray-900 hover:border-gray-500'
                                        }`}
                                    >
                                        {badge && (
                                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full ${badge.className}`}>
                                                    <StarIcon className="h-3 w-3" />
                                                    {badge.label}
                                                </span>
                                            </div>
                                        )}

                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <h3 className="text-white font-semibold text-lg">{plan.name}</h3>
                                                {spotsRemaining !== null && (
                                                    <p className="text-yellow-400 text-xs mt-0.5">
                                                        {spotsRemaining} spot{spotsRemaining !== 1 ? 's' : ''} remaining
                                                    </p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <span className="text-3xl font-bold text-white">{priceDisplay}</span>
                                                <span className="text-gray-400 text-sm block">{priceSuffix}</span>
                                            </div>
                                        </div>

                                        <ul className="space-y-2">
                                            {(plan.features || []).map((feature, i) => (
                                                <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                                                    <CheckIcon className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>

                                        <div className="mt-4 flex items-center gap-2">
                                            <input
                                                type="radio"
                                                name="plan"
                                                value={plan.id}
                                                checked={isSelected}
                                                onChange={() => setSelectedPlan(plan.id)}
                                                className="w-4 h-4 text-emerald-600"
                                                readOnly
                                            />
                                            <span className="text-sm text-gray-300">
                                                {isSelected ? 'Selected' : 'Select this plan'}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {founderFreeSpotsRemaining === 0 && earlybirdSpotsRemaining === 0 && (
                        <p className="text-center text-gray-400 text-sm mt-3">
                            All limited offer spots have been taken. Standard pricing of £3.99/month applies.
                        </p>
                    )}
                </div>

                {/* Registration Form */}
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <UserIcon className="h-6 w-6 text-emerald-400" />
                        <h2 className="text-xl font-semibold text-white">Your Profile Details</h2>
                    </div>

                    {!isLoggedIn && (
                        <div className="bg-yellow-900/20 border border-yellow-700/40 text-yellow-300 px-4 py-3 rounded-lg mb-6 text-sm">
                            You need to be logged in to join the directory.{' '}
                            <Link to="/login?redirect=/pt-directory-signup" className="underline hover:text-yellow-200">
                                Log in or create an account
                            </Link>{' '}
                            to continue.
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-900/20 border border-red-800/30 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Full Name *</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    required
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    placeholder="Your name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Contact Email *</label>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    required
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    placeholder="your@email.com"
                                />
                                <p className="text-gray-500 text-xs mt-1">Client enquiries will be sent to this address.</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="md:col-span-2">
                                <CountryCitySelect
                                    country={form.country}
                                    city={form.city}
                                    onCountryChange={(val) => handleChange('country', val)}
                                    onCityChange={(val) => handleChange('city', val)}
                                    cityLabel="City / Town *"
                                    cityRequired
                                    fieldClass="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    labelClass="block text-sm font-medium text-gray-300 mb-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Postcode</label>
                                <input
                                    type="text"
                                    value={form.postcode}
                                    onChange={(e) => handleChange('postcode', e.target.value)}
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    placeholder="e.g. SW1A 1AA"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Specialisms</label>
                            <input
                                type="text"
                                value={form.specialisms}
                                onChange={(e) => handleChange('specialisms', e.target.value)}
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                placeholder="e.g. Weight Loss, HIIT, Strength Training (comma separated)"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">About You</label>
                            <textarea
                                value={form.bio}
                                onChange={(e) => handleChange('bio', e.target.value)}
                                rows={4}
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                                placeholder="Tell potential clients about yourself, your experience, and how you can help them reach their goals..."
                            />
                        </div>

                        {/* Cost Band */}
                        <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <CurrencyPoundIcon className="h-5 w-5 text-emerald-400" />
                                <h3 className="text-white font-medium">Session Pricing</h3>
                                <span className="text-gray-500 text-xs">(optional)</span>
                            </div>

                            {/* Price type selector */}
                            <div className="flex gap-4 mb-4">
                                {[
                                    { value: 'from',  label: 'From £...' },
                                    { value: 'range', label: 'Between £... and £...' },
                                ].map(opt => (
                                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="priceType"
                                            value={opt.value}
                                            checked={form.priceType === opt.value}
                                            onChange={() => handleChange('priceType', opt.value)}
                                            className="w-4 h-4 text-emerald-600"
                                        />
                                        <span className="text-gray-300 text-sm">{opt.label}</span>
                                    </label>
                                ))}
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="priceType"
                                        value=""
                                        checked={form.priceType === ''}
                                        onChange={() => handleChange('priceType', '')}
                                        className="w-4 h-4 text-emerald-600"
                                    />
                                    <span className="text-gray-300 text-sm">Prefer not to say</span>
                                </label>
                            </div>

                            {form.priceType === 'from' && (
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-400 text-sm">From</span>
                                    <div className="relative w-36">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={form.priceFrom}
                                            onChange={(e) => handleChange('priceFrom', e.target.value)}
                                            className="w-full pl-7 pr-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <span className="text-gray-400 text-sm">per session</span>
                                </div>
                            )}

                            {form.priceType === 'range' && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-gray-400 text-sm">Between</span>
                                    <div className="relative w-32">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={form.priceFrom}
                                            onChange={(e) => handleChange('priceFrom', e.target.value)}
                                            className="w-full pl-7 pr-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <span className="text-gray-400 text-sm">and</span>
                                    <div className="relative w-32">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={form.priceTo}
                                            onChange={(e) => handleChange('priceTo', e.target.value)}
                                            className="w-full pl-7 pr-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <span className="text-gray-400 text-sm">per session</span>
                                </div>
                            )}
                        </div>

                        {/* Extra Services */}
                        <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-5">
                            <h3 className="text-white font-medium mb-1">Additional Services Offered</h3>
                            <p className="text-gray-500 text-xs mb-4">Select all that apply — clients can filter the directory by these.</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {SERVICE_OPTIONS.map(({ key, label }) => {
                                    const checked = selectedServices.includes(key);
                                    return (
                                        <label
                                            key={key}
                                            className={`flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2 transition-colors text-sm ${
                                                checked
                                                    ? 'border-emerald-500 bg-emerald-900/20 text-emerald-300'
                                                    : 'border-gray-600 bg-gray-800 text-gray-300 hover:border-gray-500'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleService(key)}
                                                className="w-4 h-4 text-emerald-600 rounded"
                                            />
                                            {label}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {selectedPlanDetails && (
                            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                                <p className="text-gray-300 text-sm">
                                    Selected plan: <span className="text-white font-medium">{selectedPlanDetails.name}</span> —{' '}
                                    <span className="text-emerald-400 font-semibold">
                                        {selectedPlanDetails.price === 0 ? 'FREE for life' : `£${selectedPlanDetails.price}/month`}
                                    </span>
                                </p>
                                {(selectedPlan === 'pt-directory-founder' || selectedPlan === 'pt-directory-earlybird') && (
                                    <p className="text-yellow-400 text-xs mt-1">
                                        🔒 This price is locked in for life — you'll receive a confirmation email as proof.
                                    </p>
                                )}
                                <p className="text-gray-500 text-xs mt-1">
                                    Payment processing will be set up after account creation.
                                </p>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting || loading}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-3 rounded-lg font-semibold transition-colors text-lg"
                        >
                            {submitting ? 'Registering...' : 'Join the PT Directory'}
                        </button>
                    </form>
                </div>

                {/* Already listed? */}
                <p className="text-center text-gray-400 text-sm mt-6">
                    Already listed?{' '}
                    <Link to="/find-pt" className="text-emerald-400 hover:text-emerald-300">
                        View the PT directory
                    </Link>
                </p>
            </div>
        </PublicLayoutWrapper>
    );
};

export default PTDirectorySignup;

